import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { RoleService } from '../services/roles.service';
import { catchError, map, Observable, of } from 'rxjs';

/**
 * Protect routes based on Roles. If you specify this in the route table,
 * then you MUST specify at least one role in the route data like this:
 * @example
 * ```ts
 * {
 *   path: "",
 *   children: [
 *     {
 *       path: "legal",
 *       canActivate: [autoLoginPartialRoutesGuard, RoleGuard],
 *       data: {
 *         roles: [AllUserRoles.DEVTEAM] // <-- Only Devteam for now?
 *       },
 *       loadChildren: () => import("./legal/legal.routes").then((m) => m.legalRoutes),
 *     }
 *   ]
 * },
 * ```
 */
@Injectable({ providedIn: 'root' })
export class RoleGuard implements CanActivate {
  private readonly roleService = inject(RoleService);

  constructor(private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const requiredRoles = this.normalizeValues(route.data['roles']);
    const requiredPermissions = this.normalizeValues(route.data['permissions']);

    return this.roleService.ensureAccessProfileLoaded().pipe(
      map((profile) => {
        if (!profile) {
          if (this.roleService.accessProfileState() === 'unauthorized') {
            this.navigateToUnauthorized(route, state);
          } else {
            this.navigateToLogin(route, state);
          }

          return false;
        }

        const hasRequiredRole =
          requiredRoles.length === 0 || this.roleService.hasAnyRole(requiredRoles);
        const hasRequiredPermission =
          requiredPermissions.length === 0 || this.roleService.hasAnyPermission(requiredPermissions);
        const allowed = hasRequiredRole && hasRequiredPermission;

        if (!allowed) {
          this.navigateToUnauthorized(route, state);
        }

        return allowed;
      }),
      catchError(err => {
        console.error('Error checking access profile:', err);
        this.navigateToLogin(route, state);
        return of(false);
      })
    );
  }

  private navigateToLogin(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): void {
    const attempted = this.resolveAttemptedUrl(route, state);
    void this.router.navigate(['/login'], {
      queryParams: { returnUrl: attempted },
      replaceUrl: true,
    });
  }

  private navigateToUnauthorized(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): void {
    const attempted = this.resolveAttemptedUrl(route, state);
    const from = typeof document !== 'undefined' ? document.referrer : undefined;
    void this.router.navigate(['/unauthorized'], {
      replaceUrl: true,
      state: { attemptedUrl: attempted, from },
    });
  }

  private resolveAttemptedUrl(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): string {
    const attempted =
      state.url ||
      this.router.getCurrentNavigation()?.finalUrl?.toString() ||
      route.url.map(segment => segment.path).join('/') ||
      '/';

    return attempted.startsWith('/') ? attempted : `/${attempted}`;
  }

  private normalizeValues(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0);
    }

    if (typeof value === 'string' && value.trim().length > 0) {
      return [value];
    }

    return [];
  }
}


/*
  The below are attempts to use the more modern (16+) function-based-with-signals approach to a route guard.
  However, they all have the same problem which is that they return before the role service has had a chance
  to do it's thing and so they always go to unauthorized or blank or whatever. Apparently the Observable
  approach waits for the role$ so that it's guaranteed to have roles before it lets the page render.

  Next idea when I have time. Just like with the app configuration, load the roles first with a fetch
  before the angualar app is even loaded so they are guaranteed to exist before the RoleGuard is called.

  In Bits2 this happened naturally because the roles were stored in the STS token but we're not doing that
  anymore so we need to fetch first, probably using MyAccess(). Hmmm that won't work cuz you need the STS
  token first to access the database.
*/

// export const RoleGuard: CanActivateFn = (
//     route: ActivatedRouteSnapshot,
//     state: RouterStateSnapshot
// ) => {

//     const requiredRoles = route.data['roles'] as string[] | undefined;

//     const roleService = inject(RoleService);
//     const router = inject(Router);

//     // Short circuit if no roles are passed
//     if (!requiredRoles || requiredRoles.length === 0) return of(true);

//     try {
//       const canActivate = roleService.hasAnyRoleSignal(requiredRoles)();
//       if (!canActivate) {
//         const attempted = router.getCurrentNavigation()?.finalUrl?.toString() || route.url.map(s => s.path).join('/') || '/';
//         const from = typeof document !== 'undefined' ? document.referrer : undefined;
//         void router.navigate(['/unauthorized'], { state: { attemptedUrl: attempted, from } });
//       }
//       return of(canActivate);
//     } catch (err) {
//       console.error('Error checking roles:', err);
//       void router.navigate(['/unauthorized']);
//       return of(false);
//     }

// };

// export const RoleGuardx: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) =>
// {
//   const requiredRoles = route.data?.['roles'] as string[] | undefined;

//   const roleService = inject(RoleService);
//   const router = inject(Router);

//   // Wait until roles are loaded (doesn't really wait, just returns false.)
//   const myRoles = roleService.roles();
//   if (!myRoles || myRoles.length < 1) return false; // or return true if you want optimistic loading }

//   if (!requiredRoles || requiredRoles.length === 0) {
//     return true;
//   }

//   try {
//     const allowed = roleService.hasAnyRoleSignal(requiredRoles)();

//     if (allowed) {
//       return true;
//     }

//     // const attempted =
//     //   router.getCurrentNavigation()?.finalUrl?.toString() ||
//     //   route.url.map(s => s.path).join('/') ||
//     //   '/';

//     const from =
//       typeof document !== 'undefined' ? document.referrer : undefined;

//     // Must use navigate() if you want to pass state
//     void router.navigate(['/unauthorized'], {
//       //state: { attemptedUrl: attempted, from }
//     });

//     return false;

//   } catch (err) {
//     console.error('Error checking roles:', err);
//     void router.navigate(['/unauthorized']);
//     return false;
//   }
// };
